// D41: the on-field scoreboard — team chips, score, clock, phase tag — replacing the old one-line
// text HUD. ui/ touches only the DOM with injected data (T-ARCH-004).
//
// D157 (brand slice 1): the bar adopts the handoff's signature scoreboard spec — one flat --bg-panel
// plate, hard hairline divisions between cells, the score in the display face, the clock in amber mono,
// team chips as colour blocks with letter-spaced tags. The phase tag is the loud caps-tag, STAMINA the
// quiet one, and the D55 red-card tally trades the 🟥 emoji for --card-red inline squares (deliberately
// NOT --team-red: a red card must never read as "the red team").

const PHASE_LABEL: Record<string, string> = {
  KICKOFF: 'KICK-OFF',
  RESTART_PENDING: '', // restart banners already announce these
  GOAL_CELEBRATION: 'GOAL!',
  HALFTIME: 'HALF-TIME',
  FULLTIME: 'FULL-TIME',
};

export class Scoreboard {
  private readonly doc: Document;
  private readonly el: HTMLDivElement;
  private readonly homeScore: HTMLSpanElement;
  private readonly awayScore: HTMLSpanElement;
  private readonly clock: HTMLSpanElement;
  private readonly tag: HTMLDivElement;
  private readonly stamina: HTMLDivElement;
  private homeCards!: HTMLSpanElement; // D55: red-card tallies
  private awayCards!: HTMLSpanElement;

  constructor(doc: Document, teamColors: [number, number], names: [string, string] = ['BLU', 'RED']) {
    this.doc = doc;
    const hex = (n: number): string => `#${n.toString(16).padStart(6, '0')}`;
    this.el = doc.createElement('div');
    this.el.id = 'fafi-scoreboard'; // D159: the touch layer compacts the board via this hook
    this.el.style.cssText =
      'position:fixed;top:10px;left:50%;transform:translateX(-50%);display:flex;flex-direction:column;align-items:center;' +
      'gap:3px;z-index:55;pointer-events:none;font-family:var(--font-ui);user-select:none';
    const bar = doc.createElement('div');
    bar.style.cssText =
      'display:flex;align-items:stretch;border-radius:6px;overflow:hidden;box-shadow:var(--shadow-hud);' +
      'background:var(--bg-panel);border:1px solid var(--border-strong)';
    const chip = (label: string, color: string): HTMLSpanElement => {
      const c = doc.createElement('span');
      c.style.cssText = `display:flex;align-items:center;padding:8px 12px;font-weight:700;font-size:12px;letter-spacing:.14em;color:#fff;background:${color}`;
      c.textContent = label;
      return c;
    };
    const scoreCell = (): HTMLSpanElement => {
      const c = doc.createElement('span');
      c.style.cssText =
        'display:flex;align-items:center;justify-content:center;padding:4px 14px;font-family:var(--font-display);font-size:22px;color:var(--ink-1);min-width:16px';
      c.textContent = '0';
      return c;
    };
    this.homeScore = scoreCell();
    this.awayScore = scoreCell();
    // D55: red-card tallies — --card-red squares per send-off, beside each team chip (D157: no emoji)
    const cardCell = (): HTMLSpanElement => {
      const c = doc.createElement('span');
      c.style.cssText = 'display:none;align-items:center;gap:2px;padding:0 6px;font-family:var(--font-mono);font-weight:600;font-size:11px;color:var(--ink-1)';
      return c;
    };
    this.homeCards = cardCell();
    this.awayCards = cardCell();
    this.clock = doc.createElement('span');
    // hard hairline divisions either side of the clock — the reference bar's only internal strokes
    this.clock.style.cssText =
      'display:flex;align-items:center;padding:8px 12px;font-family:var(--font-mono);font-weight:600;font-size:14px;color:var(--accent);' +
      'border-left:1px solid var(--border-hairline);border-right:1px solid var(--border-hairline)';
    this.clock.textContent = '00:00';
    bar.append(chip(names[0], hex(teamColors[0])), this.homeCards, this.homeScore, this.clock, this.awayScore, this.awayCards, chip(names[1], hex(teamColors[1])));
    this.tag = doc.createElement('div');
    this.tag.className = 'fafi-tag fafi-tag--loud'; // KICK-OFF / GOAL! / HALF-TIME / FULL-TIME
    this.tag.style.display = 'none';
    // D65d: a bigger, LABELED stamina bar — the 120×4 slim line was near-invisible. 200×10 with a
    // "STAMINA" caption in the quiet caps-tag motif; the fill drops to --card-red when low (< 30%).
    // D157: the row sits on its own HUD plate — bare text over live sky/grass is illegible (§3.6).
    const stamRow = doc.createElement('div');
    stamRow.style.cssText =
      'display:flex;align-items:center;gap:6px;margin-top:1px;background:rgba(8,18,12,.88);border:1px solid var(--border-hairline);border-radius:999px;padding:3px 10px';
    const stamLabel = doc.createElement('span');
    stamLabel.className = 'fafi-tag';
    stamLabel.textContent = 'STAMINA';
    stamLabel.style.fontSize = '10px'; // HUD scale of the motif
    this.stamina = doc.createElement('div');
    this.stamina.style.cssText =
      'width:200px;height:10px;border-radius:5px;background:var(--bg-panel);border:1px solid var(--border-strong);overflow:hidden';
    const fill = doc.createElement('div');
    fill.className = 'fafi-meter-fill';
    this.stamina.appendChild(fill);
    stamRow.append(stamLabel, this.stamina);
    this.el.append(bar, this.tag, stamRow);
    doc.body.appendChild(this.el);
  }

  update(
    score: [number, number],
    clockDisplay: string,
    phase: string,
    stamina01: number,
    redCards: [number, number] = [0, 0],
    returnSecs: [number, number] = [0, 0], // D65c: seconds until each team's suspended player returns (0 = none)
  ): void {
    this.homeScore.textContent = String(score[0]);
    this.awayScore.textContent = String(score[1]);
    // D55: one card mark per send-off — the scoreboard explains WHY a player stands off the pitch.
    // D65c: a red is a 20 s suspension, so a countdown rides the tally — the sent-off player sees when
    // he's back on. D157: the mark is a --card-red inline square (icon register), capped at three
    // squares then a ×N count.
    const setCards = (el: HTMLSpanElement, n: number, secs: number): void => {
      el.replaceChildren(); // clear the stale tally/countdown when the player is back on
      if (n <= 0) {
        el.style.display = 'none';
        return;
      }
      el.style.display = 'flex';
      for (let i = 0; i < Math.min(n, 3); i++) {
        const sq = this.doc.createElement('span');
        sq.className = 'fafi-cardmark';
        sq.style.cssText = 'width:8px;height:11px;border-radius:2px;background:var(--card-red);display:inline-block';
        el.append(sq);
      }
      const text = [n > 3 ? `×${n}` : '', secs > 0 ? `${Math.ceil(secs)}s` : ''].filter(Boolean).join(' ');
      if (text) {
        const t = this.doc.createElement('span');
        t.textContent = text;
        el.append(t);
      }
    };
    setCards(this.homeCards, redCards[0], returnSecs[0]);
    setCards(this.awayCards, redCards[1], returnSecs[1]);
    // D144: an EMPTY clockDisplay means "this mode has no clock" (practice), not "the clock reads
    // zero" — so hide it rather than falling back to 00:00, which is what a stopped match looks like.
    this.clock.style.display = clockDisplay === '' ? 'none' : '';
    if (clockDisplay !== '') this.clock.textContent = clockDisplay;
    const label = PHASE_LABEL[phase];
    if (label) {
      this.tag.textContent = label;
      this.tag.style.display = 'block';
    } else {
      this.tag.style.display = 'none';
    }
    const fill = this.stamina.firstElementChild as HTMLDivElement;
    fill.style.width = `${Math.round(Math.max(0, Math.min(1, stamina01)) * 100)}%`;
    fill.classList.toggle('fafi-meter-fill--low', stamina01 < 0.3); // D65d: clearly --card-red when low (< 30%)
  }
}

/** D41: transient start toast — the one-time pointer hint that replaced the permanent help line. */
/** A transient bottom toast that auto-dismisses after `ms`. Returns a dismiss() to hide it EARLY — e.g.
 *  the moment the mouse is captured, so "click to capture" never lingers as a pseudo-banner (D44 Item 10). */
export function startToast(doc: Document, text: string, ms = 4500): () => void {
  const t = doc.createElement('div');
  t.style.cssText =
    'position:fixed;bottom:12%;left:50%;transform:translateX(-50%);padding:9px 22px;border-radius:999px;' +
    'background:rgba(8,18,12,.88);color:var(--ink-1);font-family:var(--font-ui);font-weight:600;font-size:15px;z-index:80;pointer-events:none;' +
    'transition:opacity .4s;border:1px solid var(--border-hairline)';
  t.textContent = text;
  doc.body.appendChild(t);
  let done = false;
  const hide = (): void => {
    if (done) return;
    done = true;
    t.style.opacity = '0';
    setTimeout(() => t.remove(), 450);
  };
  setTimeout(hide, ms);
  return hide;
}
